import * as XLSX from "xlsx";
import {
  findCellWithValueInRange,
  getMergedCellValue,
} from "../../../../../app/utils/process.excel.helper";

// Shared showSnackbar function reference
let showSnackbar = null;
let currencies = null;

const CURRENCY = "Currency";
const SITE_CONTRACT_DATE = "Contract Effective Date";
const TOTAL_COST_PER_VISIT = "Total Cost Per Visit";
const PREDICT_VISIT_NAME = "Predict Visit Name";
const TOTAL = "Total";
const ERRORS = {
  MISSING_PREDICT_VISIT_NAME: `The '${PREDICT_VISIT_NAME}' row or row label is missing from the 'Per Procedure' sheet.`,
  MISSING_TOTAL_COST_PER_VISIT: `The '${TOTAL_COST_PER_VISIT}' row or row label is missing from the 'Per Procedure' sheet.`,
  VISIT_NAME_COST_MISMATCH: "The number of visit names doesn't match the number of visit costs in the 'Per Procedure' sheet.",
  INVALID_CURRENCY: "The currency selected in the upload file is not a valid currency in Predict.",
  INVALID_SITE_CONTRACT_DATE: "The Contract Effective Date is required. Once uploaded, enter the date for the site.",
  INVALID_DATE_RANGE: "The Contract Effective Date is invalid. Please check the month and day values.",
  VISIT_MISMATCH_BETWEEN_SHEET_PREDICT: "The visit names entered in the 'Predict Visit Names' row don't match with the expected visit names in Predict on the 'Per Procedure' sheet. Costs will not be uploaded.",
};

// Main processing function
export const processPerProcedureData = (worksheet, showSnackbarFn, visits, currenciesList) => {
  // Initialize the shared showSnackbar function
  showSnackbar = showSnackbarFn;
  currencies = currenciesList;

  // Array to collect all errors
  const errors = [];

  // Get sheet dimensions
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  const maxRow = range.e.r;
  const maxCol = range.e.c;
  const predictVisitNames = visits["study"]?.map(v => v.visitName) || [];

  try {
    const predictVisitCell = getPredictVisitCell(worksheet, maxRow, errors);
    const currency = getCurrency(worksheet, maxRow);
    const siteDate = getSiteContractDate(worksheet, maxRow);
    const costCell = getCostCell(worksheet, maxRow, errors);
    const costsRowIndex = costCell?.row;
    const visitsRowIndex = predictVisitCell?.row;
    const { visitData, mismatchCount } = getVisitNamesAndCosts(worksheet, maxCol, visitsRowIndex, costsRowIndex);

    // Validate that we have matching numbers of names and costs
    if (mismatchCount > 1) {
      showSnackbar(ERRORS.VISIT_NAME_COST_MISMATCH, "error");
      errors.push(new PerProcedureError(ERRORS.VISIT_NAME_COST_MISMATCH));
    } else if (visitData &&
      JSON.stringify(predictVisitNames.sort()) !== JSON.stringify(Object.keys(visitData).sort())
    ) {
      showSnackbar(ERRORS.VISIT_MISMATCH_BETWEEN_SHEET_PREDICT, "error");
      errors.push(new PerProcedureError(ERRORS.VISIT_MISMATCH_BETWEEN_SHEET_PREDICT));
    }

    // If we have errors, throw them all at once
    if (errors.length > 0) {
      // Create a composite error with all error messages
      const errorMessages = errors.map(error => error.message).join('; ');
      throw new PerProcedureError(errorMessages);
    }

    const visits = getVisits(visitData, currency, siteDate);
    const assessments = getAssessments(worksheet, maxRow, currency);
    return { assessments, visits, currency, siteDate };
  } catch (error) {
    // Only handle our predefined business logic errors
    if (error instanceof PerProcedureError) {
      return { error: error.message };
    }
    // For unexpected system errors, re-throw to be handled at a higher level
    throw error;
  }
};

// Find the "Predict Visit Name" cell, checking all cells in first few columns
const getPredictVisitCell = (worksheet, maxRow, errors = []) => {
  const visitCell = findCellWithValueInRange(
    worksheet,
    PREDICT_VISIT_NAME,
    0,
    maxRow,
    0,
    3, // Check columns A through D (0-3)
  );
  if (!visitCell) {
    showSnackbar(ERRORS.MISSING_PREDICT_VISIT_NAME, "error");
    errors.push(new PerProcedureError(ERRORS.MISSING_PREDICT_VISIT_NAME));
    return null;
  }
  return visitCell;
};

// Get currency from the worksheet
const getCurrency = (worksheet, maxRow) => {
  const currencyCell = findCellWithValueInRange(
    worksheet,
    CURRENCY,
    0,
    maxRow,
    0,
    3, // Check columns A through D (0-3)
  );
  const currencyAddress = XLSX.utils.encode_cell({ r: currencyCell?.row, c: currencyCell?.col + 1 });
  const currencyValue = getMergedCellValue(worksheet, currencyAddress);
  const currency = currencies?.find(c => c.currencyName === currencyValue)?.currencyCode || "";
  if (!currency) {
    showSnackbar(ERRORS.INVALID_CURRENCY, "warning");
  }
  return currency;
};

// Get site contract date from the worksheet
export const getSiteContractDate = (worksheet, maxRow) => {
  const dateCell = findCellWithValueInRange(
    worksheet,
    SITE_CONTRACT_DATE,
    0,
    maxRow,
    0,
    3, // Check columns A through D (0-3)
  );
  
  if (!dateCell) {
    return null;
  }
  
  const dateAddress = XLSX.utils.encode_cell({ r: dateCell.row + 1, c: dateCell.col });
  const dateValue = getMergedCellValue(worksheet, dateAddress);
  
  if (!dateValue) {
    showSnackbar(ERRORS.INVALID_SITE_CONTRACT_DATE, "warning");
    return null;
  }

  // dateValue can be a Date object, a numeric serial, or a string (M/D/YYYY)
  let month, day, year;

  if (dateValue instanceof Date) {
    // Excel already parsed it into a Date object
    month = dateValue.getMonth() + 1;
    day = dateValue.getDate();
    year = dateValue.getFullYear();
  } else if (typeof dateValue === "string") {
    // Parse manually so range errors are caught before Date construction
    const match = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) {
      return null;
    }
    month = parseInt(match[1], 10);
    day = parseInt(match[2], 10);
    year = parseInt(match[3], 10);
  } else {
    return null;
  }

  // Validate month and day ranges
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    showSnackbar(ERRORS.INVALID_DATE_RANGE, "warning");
    return null;
  }

  // Convert to YYYY-MM-DD format
  const formattedMonth = String(month).padStart(2, '0');
  const formattedDay = String(day).padStart(2, '0');
  const formattedDate = `${year}-${formattedMonth}-${formattedDay}`;

  return formattedDate;
};

// Find the visit costs row (where "Total Cost Per Visit" appears)
const getCostCell = (worksheet, maxRow, errors = []) => {
  const costCell = findCellWithValueInRange(
    worksheet,
    TOTAL_COST_PER_VISIT,
    0,
    maxRow,
    0,
    3, // Check columns A through D (0-3)
  );
  if (!costCell) {
    showSnackbar(ERRORS.MISSING_TOTAL_COST_PER_VISIT, "error");
    errors.push(new PerProcedureError(ERRORS.MISSING_TOTAL_COST_PER_VISIT));
    return null;
  }
  return costCell;
};

// Get visit names starting from column E (index 4)
const getVisitNamesAndCosts = (worksheet, maxCol, visitsRowIndex, costsRowIndex) => {
  if (!visitsRowIndex || !costsRowIndex) {
    return { visitData: null, mismatchCount: 0 };
  }
  const visitData = {};
  let mismatchCount = 0;

  for (let col = 4; col < maxCol; col++) {
    let cellAddress = XLSX.utils.encode_cell({ r: visitsRowIndex, c: col });
    const cellValue = getMergedCellValue(worksheet, cellAddress);

    cellAddress = XLSX.utils.encode_cell({ r: costsRowIndex, c: col });
    const costValue = getMergedCellValue(worksheet, cellAddress);

    // Skip null values and the "Total" column (usually the last one)
    if (cellValue && cellValue !== TOTAL) {
      // Parse cost value
      const cost = (typeof costValue === "number") ? costValue : parseFloat(costValue);
      if (isNaN(cost)) {
        mismatchCount++;
      } else {
        visitData[cellValue] = cost;
      }
    } else if (!cellValue && costValue) {
      mismatchCount++;
    }
  }

  return { visitData, mismatchCount };
};

// Create visits array from visit data object
const getVisits = (visitData, currency) => {
  const visits = [];
  if (!visitData || Object.keys(visitData).length === 0) {
    return visits;
  }
  Object.entries(visitData).forEach(([visitName, visitCost]) => {
    visits.push({
      visitName,
      visitCost,
      currency: currency,
    });
  });
  return visits;
};

// Get assessments from the worksheet
const getAssessments = (worksheet, maxRow, currency) => {
  const assessments = [];
  // Process assessments (any row with a value in column A)
  for (let row = 0; row <= maxRow; row++) {
    const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 }); // Column A
    const assessmentName = getMergedCellValue(worksheet, cellAddress);

    if (assessmentName) {
      const costCellAddress = XLSX.utils.encode_cell({ r: row, c: 3 }); // Column D
      const costValue = getMergedCellValue(worksheet, costCellAddress);
      const assessmentCost = typeof costValue === "number" ? costValue : parseFloat(costValue);

      if (!isNaN(assessmentCost)) {
        assessments.push({
          assessmentName,
          assessmentCost,
          currency,
        });
      }
    }
  }
  return assessments;
};

// Custom error class for predefined business logic errors
class PerProcedureError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PerProcedureError';
  }
};
